import { BooleanInput } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Router } from '@angular/router';
import { FuseDrawerService } from '@fuse/components/drawer';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { isPlatformAdmin, operatesAsTrader, storedUserIsPlatformAdmin } from 'app/core/user/domain-roles';
import { McpRealtimeRouterService } from 'app/core/services/realtime-segments/mcp-realtime-router.service';
import { environment } from 'environments/environment';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'user',
    standalone: true,
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    imports: [
        CommonModule,
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        MatDividerModule,
        MatSlideToggleModule,
    ],
})
export class UserComponent implements OnInit, OnDestroy {
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    static readonly AGENT_ENABLED_KEY = 'agent1.enabled';

    @Input() showAvatar: boolean = true;
    user: User;
    isTrader: boolean = false;
    agentEnabled: boolean = false;
    signOutRequested: boolean = false;

    /** GTX print-station helper agent download (shown when the feature is on). */
    readonly gtxEnabled = (environment as any)?.gtxAgent?.enabled === true;
    readonly gtxAgentDownloadUrl = `${environment.apiUrl}/download/gtx-agent`;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService,
        private _fuseDrawerService: FuseDrawerService,
        private _mcpRealtime: McpRealtimeRouterService
    ) {
        this.agentEnabled = localStorage.getItem(UserComponent.AGENT_ENABLED_KEY) === 'true';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;
                // Check if user operates as a Trader (role or assistant flag)
                this.isTrader = operatesAsTrader(user);

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this.onAccountMenuClosed();
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * While the account menu is open, keep the Quick Chat rail from stealing
     * clicks. The profile button sits directly above that fixed right strip,
     * and the menu hangs down over it — same hit-testing issue as the chat
     * glow toast over the drawer composer.
     */
    onAccountMenuOpened(): void {
        document.body.classList.add('user-account-menu-open');
    }

    onAccountMenuClosed(): void {
        document.body.classList.remove('user-account-menu-open');
    }

    /**
     * Update the user status
     *
     * @param status
     */
    updateUserStatus(status: string): void {
        // Return if user is not available
        if (!this.user) {
            return;
        }

        // Update the user
        this._userService
            .update({
                ...this.user,
                status,
            })
            .subscribe();
    }

    /**
     * Sign out
     */
    signOut(event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();

        // Pointer-down is used by the menu item so fixed drawers cannot consume
        // the following click while the Material overlay is closing. The click
        // binding remains for keyboard activation, and this latch prevents both
        // events from starting duplicate navigations.
        if (this.signOutRequested) {
            return;
        }

        this.signOutRequested = true;
        this._router.navigate(['/sign-out']).then((navigated) => {
            if (!navigated) {
                this.signOutRequested = false;
                this._changeDetectorRef.markForCheck();
            }
        });
    }

    /**
     * Open settings drawer
     */
    openSettings(): void {
        const drawer = this._fuseDrawerService.getComponent('settingsDrawer');
        if (drawer) {
            drawer.toggle();
        }
    }

    /**
     * Open profile drawer
     */
    openProfile(): void {
        window.dispatchEvent(new CustomEvent('open-profile-drawer'));
    }

    toggleAgent(enabled: boolean): void {
        if (!isPlatformAdmin(this.user)) {
            return;
        }
        this.agentEnabled = enabled;
        localStorage.setItem(UserComponent.AGENT_ENABLED_KEY, String(enabled));
        window.dispatchEvent(new CustomEvent('agent1-toggle', { detail: { enabled } }));

        if (enabled) {
            this._mcpRealtime.prewarm().catch(() => {});
        } else {
            this._mcpRealtime.stop();
        }
    }

    static isAgentEnabled(): boolean {
        return storedUserIsPlatformAdmin()
            && localStorage.getItem('agent1.enabled') === 'true';
    }
}
